import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchShopById } from '../services/shopService';
import { createCheckoutSession, reactivateSubscription } from '../services/paymentService';
import NewPricingPlans from '../components/NewPricingPlans';
import { isAuthenticated, getCurrentUser } from '../services/authService';
import { message, Button, Spin, Alert, Typography, Tag } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import './ChangePlanPage.css';

const { Title, Paragraph, Text } = Typography;

const ChangePlanPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [shop, setShop] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [selectedPlanData, setSelectedPlanData] = useState(null);
    const [isReactivating, setIsReactivating] = useState(false);
    const [isDemoMode] = useState(
        process.env.REACT_APP_SKIP_PAYMENT === 'true' ||
        process.env.REACT_APP_DEBUG_MODE === 'true'
    );

    useEffect(() => {
        const checkAuthAndLoadShop = async () => {
            try {
                setLoading(true);
                setError(null);
                const authenticated = await isAuthenticated();
                if (!authenticated) {
                    navigate('/login', { state: { from: `/dashboard/change-plan/${id}` } });
                    return;
                }
                const currentUser = await getCurrentUser();
                const response = await fetchShopById(id);

                if (response.success && response.data) {
                    const shopData = response.data;
                    if (shopData.user !== currentUser.id) {
                        setError('Sie sind nicht berechtigt, den Tarif dieses Shops zu ändern.');
                        setLoading(false);
                        return;
                    }
                    setShop(shopData);
                } else {
                    setError(response.message || 'Fehler beim Laden der Shop-Daten');
                }
            } catch (err) {
                console.error('Error loading shop data:', err);
                setError('Fehler beim Laden der Shop-Daten. Bitte versuchen Sie es später erneut.');
            } finally {
                setLoading(false);
            }
        };
        checkAuthAndLoadShop();
    }, [id, navigate]);

    const handlePlanSelect = (selectedData) => {
        console.log('[ChangePlanPage] Plan ausgewählt:', selectedData);
        setSelectedPlanData(selectedData);
    };

    const handleReactivate = async () => {
        if (!shop || shop.subscriptionStatus !== 'cancelled') return;

        console.log('[ChangePlanPage] handleReactivate aufgerufen.');
        setIsReactivating(true);
        setError(null);
        try {
            const response = await reactivateSubscription(shop.id);
            if (response.success) {
                navigate('/dashboard', { state: { successMessage: response.message || 'Abonnement erfolgreich reaktiviert!' } });
            } else {
                setError(response.message || 'Fehler bei der Reaktivierung des Abonnements.');
            }
        } catch (err) {
            console.error('[ChangePlanPage] Kritischer Fehler bei der Reaktivierung:', err);
            setError('Fehler bei der Reaktivierung. Bitte versuchen Sie es später erneut.');
        } finally {
            setIsReactivating(false);
        }
    };

    const handleSubmit = async () => {
        console.log('[ChangePlanPage] handleSubmit aufgerufen.');
        if (!selectedPlanData) {
            console.log('[ChangePlanPage] Fehler: Plan nicht ausgewählt.');
            setError('Bitte wählen Sie einen neuen Tarif aus.');
            return;
        }
        console.log(`[ChangePlanPage] Ausgewählter Plan: ${selectedPlanData.id}, Intervall: ${selectedPlanData.interval}`);

        if (shop && selectedPlanData.id === shop.subscriptionPlan && selectedPlanData.interval === shop.subscriptionInterval && shop.subscriptionStatus !== 'cancelled') {
            console.log('[ChangePlanPage] Fehler: Aktueller Plan/Intervall bereits ausgewählt.');
            setError('Dies ist bereits Ihr aktueller Tarif mit diesem Abrechnungsintervall.');
            return;
        }

        console.log('[ChangePlanPage] Starte Tarifänderungsprozess...');
        try {
            setSubmitting(true);
            setError(null);

            if (isDemoMode) {
                console.log('[ChangePlanPage] Demo-Modus aktiv, simuliere Erfolg.');
                message.success('Demo: Tarifänderung erfolgreich simuliert!');
                setTimeout(() => {
                    navigate('/dashboard', { state: { successMessage: 'Demo: Tarifänderung erfolgreich simuliert!' } });
                }, 2000);
                return;
            }

            console.log(`[ChangePlanPage] Rufe createCheckoutSession für Shop ${id}, Plan ${selectedPlanData.id}, Intervall ${selectedPlanData.interval} auf...`);
            const response = await createCheckoutSession(id, selectedPlanData.id, selectedPlanData.interval);
            console.log('[ChangePlanPage] Antwort von createCheckoutSession:', response);

            if (response.success && response.url) {
                console.log('[ChangePlanPage] Checkout-Session erfolgreich erstellt. Leite zu Stripe weiter:', response.url);
                window.location.href = response.url;
            } else {
                console.error('[ChangePlanPage] Fehler von createCheckoutSession:', response.message);
                setError(response.message || 'Fehler beim Erstellen der Zahlungssitzung.');
            }
        } catch (err) {
            console.error('[ChangePlanPage] Kritischer Fehler in handleSubmit:', err);
            setError('Fehler beim Ändern des Tarifs. Bitte versuchen Sie es später erneut.');
        } finally {
            console.log('[ChangePlanPage] handleSubmit abgeschlossen (finally Block).');
            setSubmitting(false);
        }
    };

    const getSubscriptionTagColor = (status) => {
        switch (status) {
            case 'active':
                return 'success';
            case 'trialing':
                return 'processing';
            case 'cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    const getSubscriptionStatusText = (status) => {
        switch (status) {
            case 'active':
                return 'Aktiv';
            case 'trialing':
                return 'Testphase';
            case 'cancelled':
                return 'Gekündigt';
            default:
                return 'Inaktiv';
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <Spin size="large" tip="Lade Tarifinformationen..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="container" style={{ padding: '20px' }}>
                <Alert message="Fehler" description={error} type="error" showIcon />
                <Button onClick={() => navigate('/dashboard')} style={{ marginTop: '15px' }}>Zurück zum Dashboard</Button>
            </div>
        );
    }

    if (!shop) {
        return <div className="container" style={{ padding: '20px' }}>Shop nicht gefunden.</div>;
    }

    return (
        <div className="change-plan-page">
            <Helmet>
                <title>Tarif ändern | {shop ? shop.name : 'Shop'} | Snus Finder</title>
                <meta name="description" content={`Ändern Sie den Abonnement-Tarif für ${shop ? shop.name : 'Ihren Shop'}.`} />
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            <div className="container" style={{ maxWidth: '800px', margin: '20px auto', padding: '20px', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                <Title level={2} style={{ textAlign: 'center', marginBottom: '20px' }}>Tarif ändern für "{shop.name}"</Title>
                
                <div style={{ marginBottom: '30px', padding: '15px', border: '1px solid #eee', borderRadius: '4px' }}>
                    <Title level={4}>Aktueller Status</Title>
                    <Paragraph>
                        <Text strong>Aktueller Tarif:</Text> {shop.subscriptionPlan || 'Kein'} ({shop.subscriptionInterval || '-'})
                        <br />
                        <Text strong>Status:</Text> <Tag color={getSubscriptionTagColor(shop.subscriptionStatus)}>{getSubscriptionStatusText(shop.subscriptionStatus)}</Tag>
                        <br />
                        {shop.subscriptionEnd && shop.subscriptionStatus !== 'active' && shop.subscriptionStatus !== 'trialing' && 
                            <><Text strong>Endet am:</Text> {new Date(shop.subscriptionEnd).toLocaleDateString('de-DE')}</>}
                    </Paragraph>
                </div>

                {shop.subscriptionStatus === 'cancelled' && (
                    <Alert
                        message="Abonnement gekündigt"
                        description={
                            <span>Ihr aktuelles Abonnement wurde gekündigt und läuft noch bis zum {shop.subscriptionEnd ? new Date(shop.subscriptionEnd).toLocaleDateString('de-DE') : 'Ende des Abrechnungszeitraums'}. 
                            Sie können die Kündigung unten zurücknehmen oder direkt einen neuen Tarif auswählen, der dann im Anschluss startet.</span>
                        }
                        type="warning"
                        showIcon
                        style={{ marginBottom: '20px' }}
                    />
                )}

                <div style={{ marginBottom: '30px' }}>
                    <Title level={4}>{shop.subscriptionStatus === 'cancelled' ? 'Oder neuen Tarif wählen' : 'Neuen Tarif wählen'}</Title>
                    <NewPricingPlans 
                        onSelect={handlePlanSelect}
                        currentPlanId={shop?.subscriptionPlan}
                        currentInterval={shop?.subscriptionInterval}
                    />
                </div>
                
                <div style={{ textAlign: 'center', marginTop: '30px' }}>
                    {selectedPlanData && (shop.subscriptionPlan !== selectedPlanData.id || shop.subscriptionInterval !== selectedPlanData.interval) && (
                        <Button 
                            type="primary" 
                            onClick={handleSubmit} 
                            loading={submitting} 
                            size="large"
                            style={{ marginRight: shop.subscriptionStatus === 'cancelled' ? '15px' : '0' }}
                        >
                            {`Wechseln zu ${selectedPlanData.id === 'premium' ? 'Premium' : 'Professional'} (${selectedPlanData.interval === 'monthly' ? 'Monatlich' : 'Jährlich'})`}
                        </Button>
                    )}

                    {shop.subscriptionStatus === 'cancelled' && (
                        <Button 
                            type="default" 
                            onClick={handleReactivate} 
                            loading={isReactivating} 
                            size="large"
                            icon={<SyncOutlined />}
                        >
                            {`Aktuelles Abo (${shop.subscriptionPlan}) reaktivieren`}
                        </Button>
                    )}
                    
                    <Button onClick={() => navigate('/dashboard')} style={{ marginLeft: '10px' }} size="large">
                        Abbrechen
                    </Button>
                </div>

            </div>
        </div>
    );
};

export default ChangePlanPage;